import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import useCartStore, { cartSubtotal } from "../store/cartStore";
import { formatPrice } from "../lib/format";
import useSEO from "../hooks/useSEO";

export default function Cart() {
  useSEO({ title: "Your Cart", noindex: true });
  const items = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const subtotal = cartSubtotal(items);

  if (items.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
        <ShoppingBag size={40} className="text-mono/15" />
        <h1 className="mt-4 font-serif text-xl text-ink">Your cart is empty</h1>
        <p className="mt-2 text-sm text-ink-light">Browse the collection and add something you like.</p>
        <Link to="/categories" className="mt-6 rounded-2xl bg-[#2b2b2d] px-8 py-3 text-xs tracking-wide text-white shadow-floating dark:bg-gold-500 dark:text-ink-dark dark:shadow-gold">
          BROWSE PRODUCTS
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-32">
      <h1 className="mb-4 font-serif text-2xl text-ink">Your Cart</h1>

      <AnimatePresence initial={false}>
        <div className="space-y-4">
          {items.map((item) => (
            <motion.div
              key={item.key}
              layout
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex gap-3 rounded-2xl bg-surface p-3 shadow-card"
            >
              <img src={item.image} alt={item.name} className="h-20 w-20 rounded-xl object-cover" />
              <div className="flex flex-1 flex-col">
                <p className="text-sm text-ink">{item.name}</p>
                {item.options?.length > 0 && (
                  <p className="text-xs text-ink-light">
                    {item.options.map((o) => `${o.name}: ${o.value}`).join(" · ")}
                  </p>
                )}
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center rounded-full border border-mono/10">
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity - 1)}
                      className="flex h-7 w-7 items-center justify-center text-ink-light"
                      aria-label="Decrease quantity"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-5 text-center text-xs">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      className="flex h-7 w-7 items-center justify-center text-ink-light"
                      aria-label="Increase quantity"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <span className="text-sm text-ink">{formatPrice(item.price * item.quantity)}</span>
                </div>
              </div>
              <button
                onClick={() => removeItem(item.key)}
                aria-label="Remove item"
                className="self-start text-mono/25"
              >
                <Trash2 size={16} />
              </button>
            </motion.div>
          ))}
        </div>
      </AnimatePresence>

      {/* Portaled to <body> — this bar is `fixed`, but the page-transition
          wrapper in AppShell animates via a `transform` (Framer Motion's
          `y` offset), and any transformed ancestor becomes the containing
          block for a `fixed` descendant per the CSS spec. Left nested
          inside it, the bar would render pinned near the top of that
          ancestor for the ~300ms of the route transition, then visibly
          snap down to the real bottom once the transform clears — the
          "comes in at the top then glitches to the bottom" bug. A portal
          renders it as a direct child of <body>, outside that transform
          entirely, so it's correctly viewport-fixed from the first frame. */}
      {createPortal(
        <div className="fixed inset-x-0 bottom-[calc(64px+env(safe-area-inset-bottom))] z-40 rounded-t-3xl border-t border-mono/5 bg-surface/95 px-4 py-4 shadow-floating backdrop-blur-md">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm text-ink-light">Subtotal</span>
            <span className="text-lg text-ink">{formatPrice(subtotal)}</span>
          </div>
          <Link
            to="/checkout"
            className="block w-full rounded-2xl bg-[#2b2b2d] py-3.5 text-center text-sm tracking-wide text-white dark:bg-gold-500 dark:text-ink-dark"
          >
            PROCEED TO CHECKOUT
          </Link>
        </div>,
        document.body
      )}
    </div>
  );
}
